<template>
  <div class="preview-container">
    <table>
      <tbody>
        <tr>
          <td></td>
          <td
            v-for="colId in columnsCount"
            :key="colId"
            class="centered">
            {{ cartesianColumnToExcel(colId - 1) }}
          </td>
        </tr>
        <tr
          v-for="rowId in rowCount"
          :key="rowId">
          <td class="centered">{{ rowId }}</td>
          <td
            v-for="colId in columnsCount"
            :key="colId"
            class="cell"
            :class="{
              selectable: props.selectMode,
              selected: props.selectMode && hoveredCell?.row < rowId && hoveredCell?.column === colId,
            }"
            @mouseenter="() => onHovered({ row: rowId, column: colId })"
            @click="() => emit('cellClicked', { row: rowId - 1, column: colId - 1 })">
            {{ getCellValue(rowId, colId) }}
          </td>
        </tr>
      </tbody>
    </table>
    <a
      v-if="isTruncated"
      class="show-more"
      @click="showMore">
      +{{ lastPopulatedRowId - renderedRow }} More
    </a>
  </div>
</template>

<script setup lang="ts">
  import { Worksheet } from 'exceljs';
  import { computed, ref, watch } from 'vue';

  import { cartesianColumnToExcel } from '@/common/components/controls/table/Spreadsheet/ExcelCoordinatesHelper';

  const RENDER_INCREMENT = 5;

  interface IProps {
    worksheet: Worksheet;
    numberOfRows?: number;
    selectMode?: boolean;
  }

  const props = withDefaults(defineProps<IProps>(), { numberOfRows: 10 });

  const renderedRow = ref(props.numberOfRows);

  interface IEmits {
    (e: 'cellClicked', coordinates: { row: number; column: number }): void;
  }

  const emit = defineEmits<IEmits>();

  const hoveredCell = ref<{ row: number; column: number }>();

  const columnsCount = computed(() => props.worksheet.actualColumnCount);

  const lastPopulatedRowId = computed(() => {
    for (let rowId = props.worksheet.rowCount; rowId > 0; rowId--) {
      const row = props.worksheet.getRow(rowId);
      if (row.hasValues) {
        return rowId;
      }
    }
    return 0;
  });

  const rowCount = computed(() => {
    return Math.min(lastPopulatedRowId.value, renderedRow.value);
  });

  const isTruncated = computed(() => {
    return lastPopulatedRowId.value > renderedRow.value;
  });

  function onHovered(coordinates: { row: number; column: number }) {
    if (!props.selectMode) {
      return;
    }
    hoveredCell.value = coordinates;
  }

  function showMore() {
    renderedRow.value = renderedRow.value + RENDER_INCREMENT;
  }

  function getCellValue(rowId: number, colId: number) {
    const row = props.worksheet.getRow(rowId);
    const cell = row.getCell(colId);
    return cell.formula != null ? cell.result : cell.value;
  }

  watch(
    () => props.selectMode,
    (selectMode) => {
      if (!selectMode) {
        hoveredCell.value = undefined;
      }
    },
  );
</script>

<style lang="scss" scoped>
  .preview-container {
    overflow-x: auto;
  }

  table,
  th,
  td {
    border: rgba(0, 0, 0, 0.1) solid 1px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  td {
    padding: 0.5rem 1rem;
  }

  .centered {
    text-align: center;
  }

  .cell {
    text-overflow: ellipsis;

    &.selectable:hover {
      cursor: pointer;
      background-color: var(--primaryColor);
      color: var(--primaryColorReverse);
    }

    &.selected {
      background-color: var(--primaryColor);
      color: var(--primaryColorReverse);
      opacity: 0.5;
    }
  }

  .show-more {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    border: rgba(0, 0, 0, 0.1) solid 1px;
    border-top: none;
    height: 3rem;
  }
</style>
